import type { Photo } from '@/content/sections'
import type { Report } from '@/content/reports'
import { LINKS } from '@/content/pages/links'
import { DAIMI_SLUG } from '@/content/daimi'

/**
 * The two bands every report page closes with — the methodology separator and
 * the global-perspective card.
 *
 * **Why this is one module rather than a copy per page.** The six designed
 * Industry Focus pages each grew their own `_METHODOLOGY` and `_GLOBAL`
 * constants, transcribed from their own reference screenshot, and they drifted:
 * the same sentence read "1,300+ assessments" on Airports and Airlines and
 * "1,500+" on Banking and Construction, and the wheel sat on a white plate on
 * some of them because the export we held was lettered in navy. Asked for on
 * 2026-08-28 with the two bands as reference captures — *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"* — so the copy, the
 * artwork and the markup are held once here and drawn by
 * `components/ReportClosing.tsx` on every report page.
 *
 * **Three pages do not take it, by name in the request**: the Digital & AI
 * Maturity Report 2026 landing page (this band points *at* it, so it would point
 * at itself), the USA Edition and the Türkiye edition. `hasReportClosing()` is
 * that rule, read off `Report.layout`.
 *
 * The one thing the design asks for that this site declines is the reference's
 * pink: digitopia.co sets the global heading and its CTA in magenta, and
 * `src/index.css` says at the top that this site has exactly one accent. Both
 * are `text-primary`/`btn-primary` here, which is the call every other report
 * page already made about the same pink.
 */

/**
 * The methodology separator.
 *
 * The wheel sits straight on the dark band, with no white plate under it. The
 * navy-lettered `DAIMI_FRAMEWORK.photo` needed one on a dark ground or its six
 * dimensions vanished — which is what the Airports and Consumer Goods pages did,
 * and what their amber notes asked to be sent an export to fix.
 *
 * The export it uses is the transparent-background one supplied on 2026-09-01,
 * asked for so the artwork blends into `bg-ink` instead of showing the white
 * rectangle the 2026-08-28 file carried around it.
 */
export const REPORT_METHODOLOGY = {
  heading: 'The DAIMI Methodology',
  body: 'DAIMI (Digital and AI Maturity Index) is Digitopia’s framework for assessing how effectively organizations build capability and value in the digital and AI era. It measures maturity across six dimensions (Customer, Operations, Innovation, People, Governance, and Technology) to capture both business outcomes and the foundational capabilities required to deliver them consistently.',
  close: 'Discover how our methodology can transform your strategy.',
  photo: {
    src: '/photo/daimi-framework-wheel-transparent.png',
    alt: 'The DAIMI wheel: People, Customer, Operations, Innovation, Governance and Technology around the Digital & AI Maturity Index',
  } satisfies Photo,
  learnHref: LINKS.daimi,
  learnLabel: 'Learn more',
}

/** The global-perspective card, pointing back up at the full 2026 report. */
export const REPORT_GLOBAL = {
  heading: 'The Global Perspective',
  /** The opening sentence, which names the report it closes. */
  lead(name: string): string {
    return `The ${name} is part of a larger, global study on the state of transformation. Based on six years of research, 1,500+ assessments, and insights from 20+ countries and 10+ industries, the full report offers a truly global perspective.`
  },
  body: 'While AI adoption is accelerating, organizational capability is not. The “Digital & AI Maturity Report 2026” quantifies this gap and provides the strategic blueprint required to close it.',
  photo: {
    src: '/photo/daimi-report-2026-stack.webp',
    alt: 'A stack of the Digital & AI Maturity Report 2026',
  } satisfies Photo,
  ctaLabel: 'Explore the full report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_SLUG}`,
}

/**
 * The three pages the request excludes by name — the flagship 2026 report and
 * its two editions.
 */
const WITHOUT_CLOSING = new Set(['daimi', 'usa-edition', 'turkiye-edition'])

/** Whether a report's page carries the closing bands. */
export function hasReportClosing(report: Report): boolean {
  return !(report.layout && WITHOUT_CLOSING.has(report.layout))
}

/** How the global band names this report, falling back to its full title. */
export function reportClosingName(report: Report): string {
  return report.shortName ?? report.title
}

/**
 * What is still unresolved about this band, on this report's page.
 *
 * ONE QUESTION NOW, AND IT ONLY APPLIES TO THE REPORTS THAT ARE NOT CUTS OF THE
 * 2026 STUDY. The other one — the assessment count — was answered on 2026-09-01.
 *
 * It had been the largest single group of open checks on the site: 28 boxes, one
 * per report page, all asking about a number written in exactly one place. The
 * band said “1,300+ assessments” because that is what the reference capture the
 * design was copied from said, while this site's own report landing page states
 * **1,575** assessments and 33,870 participants, and two focus pages had read
 * “1,500+” before this shared band replaced their copy. So the site contradicted
 * itself three clicks apart.
 *
 * Kagan settled it: **1,500+**. It is right on all three counts that matter —
 * true against 1,575, rounded DOWN so the claim is conservative rather than
 * generous, and in the same “+” register as the rest of the sentence (20+
 * countries, 10+ industries, six years of research), which is why it reads
 * better here than the exact 1,575 would.
 *
 * `undefined` for a cut of the 2026 study: nothing about this band is open on
 * those pages any more. `reports.ts` filters on that rather than emitting an
 * empty note.
 */
export function reportClosingCheck(report: Report): string | undefined {
  if (report.partOfStudy) return undefined
  return 'This report is not an Industry Focus cut of the 2026 maturity report but a separate series, yet the band says it is “part of a larger, global study” all the same, because the request was for the identical closing on every report page bar three. Confirm that wording, or say to leave this band off the Future 2030 outlooks and Top 10 Trends.'
}
